import React from "react";
import { KpiTileSkeleton } from "./skeletons";
import Sparkline from "./primitives/Sparkline";

/**
 * KpiStrip — 5 hero KPI tiles with sparklines and 7d deltas.
 *
 * Replaces the old StatsGrid component. Click any non-Total tile to filter
 * the journey list by status (same UX as the previous StatsGrid).
 *
 * The old testid scheme is preserved verbatim on every tile:
 *   - stat-total-pnrs / stat-confirmed / stat-rac / stat-waiting / stat-cancelled
 * Additionally, each tile gets a new kpi-tile-{slug} testid for the new UI.
 *
 * Props:
 *   stats           — { total_pnrs, confirmed_seats, rac_seats, waiting_seats,
 *                       cancelled_seats, spark_total/confirmed/rac/waiting/
 *                       cancelled, deltas: { total, confirmed, rac, waiting,
 *                       cancelled } }
 *   loading         — bool
 *   onFilterClick   — (filter: "confirmed" | "rac" | "waiting" | "cancelled") => void
 */

// Tone logic per README:
//   - For Confirmed/Total: positive delta = good (emerald), zero = neutral
//   - For Waiting/Cancelled: negative delta = good (emerald), positive = bad (rose)
const deltaToneFor = (bucket, value) => {
  if (value === 0) return "neutral";
  if (bucket === "confirmed" || bucket === "total") {
    return value > 0 ? "good" : "bad";
  }
  if (bucket === "waiting" || bucket === "cancelled") {
    return value < 0 ? "good" : "bad";
  }
  return "neutral";
};

const deltaClasses = {
  good: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
  bad: "text-rose-300 bg-rose-500/10 border-rose-500/20",
  neutral: "text-zinc-400 bg-zinc-800 border-zinc-700",
};

const KpiTile = ({
  legacyTestid,
  newTestid,
  label,
  value,
  color,
  spark,
  delta,
  bucket,
  accent,
  subLabel,
  onClick,
  clickable,
}) => {
  const tone = deltaToneFor(bucket, delta || 0);
  const sign = (delta || 0) > 0 ? "+" : "";
  const Wrapper = clickable ? "button" : "div";

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      data-testid={legacyTestid}
      className={`relative group bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 overflow-hidden transition-colors text-left w-full ${
        clickable
          ? "hover:border-zinc-700 hover:bg-zinc-900/60 cursor-pointer"
          : ""
      }`}
    >
      {/* Status accent rail (3px, glowing) */}
      <span
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
        style={{ background: accent, boxShadow: `0 0 12px ${accent}55` }}
        aria-hidden="true"
      />

      {/* nested test hook for the new naming scheme */}
      <span data-testid={newTestid} className="sr-only">
        {label}
      </span>

      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10.5px] tracking-[0.18em] uppercase text-zinc-500 font-semibold font-mono">
          {label}
        </span>
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium font-mono border ${deltaClasses[tone]}`}
        >
          {sign}
          {delta ?? 0} · 7d
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <div
            className={`text-[40px] leading-none font-bold ${color} font-heading`}
            style={{ letterSpacing: "-0.02em" }}
          >
            {value}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">{subLabel}</div>
        </div>
        <div className="opacity-90 group-hover:opacity-100 transition-opacity shrink-0">
          {spark && spark.length >= 2 ? (
            <Sparkline data={spark} color={accent} width={88} height={36} />
          ) : (
            <div className="w-[88px] h-[36px]" />
          )}
        </div>
      </div>
    </Wrapper>
  );
};

const KpiStrip = ({ stats, loading = false, onFilterClick }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <KpiTileSkeleton key={i} index={i} />
        ))}
      </div>
    );
  }

  const tiles = [
    {
      legacyTestid: "stat-total-pnrs",
      newTestid: "kpi-tile-total",
      label: "Total Journeys",
      value: stats.total_pnrs ?? 0,
      color: "text-zinc-50",
      accent: "#818cf8",
      spark: stats.spark_total,
      delta: stats.deltas?.total,
      bucket: "total",
      subLabel: "Active in queue",
      clickable: false,
    },
    {
      legacyTestid: "stat-confirmed",
      newTestid: "kpi-tile-confirmed",
      label: "Confirmed",
      value: stats.confirmed_seats ?? 0,
      color: "text-emerald-400",
      accent: "#34d399",
      spark: stats.spark_confirmed,
      delta: stats.deltas?.confirmed,
      bucket: "confirmed",
      subLabel: "Seats secured",
      clickable: (stats.confirmed_seats ?? 0) > 0,
      onClick: () => onFilterClick?.("confirmed"),
    },
    {
      legacyTestid: "stat-rac",
      newTestid: "kpi-tile-rac",
      label: "RAC",
      value: stats.rac_seats ?? 0,
      color: "text-amber-400",
      accent: "#fbbf24",
      spark: stats.spark_rac,
      delta: stats.deltas?.rac,
      bucket: "rac",
      subLabel: "Cabin allocation",
      clickable: (stats.rac_seats ?? 0) > 0,
      onClick: () => onFilterClick?.("rac"),
    },
    {
      legacyTestid: "stat-waiting",
      newTestid: "kpi-tile-waiting",
      label: "Waiting List",
      value: stats.waiting_seats ?? 0,
      color: "text-rose-400",
      accent: "#fb7185",
      spark: stats.spark_waiting,
      delta: stats.deltas?.waiting,
      bucket: "waiting",
      subLabel: "Moving up",
      clickable: (stats.waiting_seats ?? 0) > 0,
      onClick: () => onFilterClick?.("waiting"),
    },
    {
      legacyTestid: "stat-cancelled",
      newTestid: "kpi-tile-cancelled",
      label: "Cancelled",
      value: stats.cancelled_seats ?? 0,
      color: "text-zinc-300",
      accent: "#a1a1aa",
      spark: stats.spark_cancelled,
      delta: stats.deltas?.cancelled,
      bucket: "cancelled",
      subLabel: "Refund pending",
      clickable: (stats.cancelled_seats ?? 0) > 0,
      onClick: () => onFilterClick?.("cancelled"),
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
      {tiles.map((t) => (
        <KpiTile key={t.legacyTestid} {...t} />
      ))}
    </div>
  );
};

export default KpiStrip;
